/**
 * The three actions on a reservations-tab row, in the order the design puts them
 * (BON-472): Resend link, Edit, Release.
 *
 * TWO RULES, NOT ONE, and they disagree exactly where it matters:
 *
 *   - Resend and Edit need a LIVE HOLD to re-arm, so they are refused on every
 *     stage the API refuses — lapsed, paying, needs-review, out of attempts.
 *   - Release is refused while money may still land on the seats, and on a row
 *     that is not holding any: it hands back what the row holds, so on a lapsed
 *     or struck-out one it returned nothing while demanding a reason for it.
 *     Held seats rather than stage, because a struck-out row whose best-effort
 *     release failed is terminal AND still holding (BON-472).
 *
 * Collapsing the two into one flag breaks whichever it is not written for.
 *
 * NOTHING RENDERS WITHOUT BOOKING-WRITE PERMISSION, Release included. Release
 * used to be drawn for anyone who could read the tab and refused by the API on
 * click — the same live-looking-control defect the New booking button already had
 * fixed. Widening the gate to cover it is deliberate (operator, 2026-08-24) and
 * goes beyond the stated criteria. The gate is applied by the CALLER, which drops
 * the whole column rather than leaving an Actions heading over empty cells.
 */
import { Bell, Pencil, X } from 'lucide-react';

import type { ReservationRow } from '../../api/reservations';

import { ReservationActionButton } from './ReservationActionButton';
import { releaseRefusalReason, rotationRefusalReason } from './reservationRotationRules';

export interface ReservationActionHandlers {
  onResend: () => void;
  onEdit: () => void;
  onRelease: () => void;
}

export function ReservationRowActions({
  row,
  onResend,
  onEdit,
  onRelease,
}: { row: ReservationRow } & ReservationActionHandlers) {
  const rotationRefusal = rotationRefusalReason(row.stage);
  // The whole row, not just its stage: Release also refuses a reservation that
  // holds no seats, which no stage can answer on its own.
  const releaseRefusal = releaseRefusalReason(row);

  return (
    <div className="flex items-center justify-end gap-1.5">
      <ReservationActionButton
        icon={<Bell size={13} aria-hidden />}
        label="Resend link"
        disabledReason={rotationRefusal}
        onClick={onResend}
      />
      <ReservationActionButton
        icon={<Pencil size={13} aria-hidden />}
        label="Edit"
        disabledReason={rotationRefusal}
        onClick={onEdit}
      />
      <ReservationActionButton
        icon={<X size={13} aria-hidden />}
        label="Release"
        tone="danger"
        disabledReason={releaseRefusal}
        onClick={onRelease}
      />
    </div>
  );
}
